import { useMemo, useState } from "react";
import { ExternalLink, FilePenLine, Globe, Lock } from "lucide-react";
import {
  WEBSITE_CONTENT_PAGES,
  type WebsiteContentEditorView,
  type WebsiteContentPage,
} from "@/lib/websiteContentPages";
import HomepagePromotionControl from "@/components/HomepagePromotionControl";

/**
 * Website Content: one place to see every public page and find where it is edited.
 *
 * This is a shell. It lists pages and sends the admin to the editor that already
 * owns that content (the Community hero and page editor, the Events editor, the
 * featured speakers editor). It saves nothing and keeps no draft of its own, so
 * there is exactly one place to change each thing and one save path to audit.
 *
 * It is rendered only inside AdminCRM, which is inside RequireAdmin and checks the
 * server role again before showing any view.
 */
type Filter = "all" | "editable" | "code";

const FILTERS: ReadonlyArray<{ id: Filter; label: string }> = [
  { id: "all", label: "All pages" },
  { id: "editable", label: "Editable here" },
  { id: "code", label: "Written in code" },
];

const AUDIENCE_LABEL: Record<WebsiteContentPage["audience"], string> = {
  public: "Public",
  members: "Members",
  legal: "Legal",
};

export default function WebsiteContentView({ onOpenEditor }: { onOpenEditor: (view: WebsiteContentEditorView) => void }) {
  const [filter, setFilter] = useState<Filter>("all");
  const pages = useMemo(
    () => WEBSITE_CONTENT_PAGES.filter(page => filter === "all" || (filter === "editable" ? page.editor !== null : page.editor === null)),
    [filter],
  );

  return (
    <section className="website-content" aria-labelledby="website-content-title" style={{ padding: "clamp(1rem,3vw,2rem)", color: "#E2E8F0", fontFamily: "'Inter', sans-serif", overflowY: "auto" }}>
      <header style={{ marginBottom: "1.25rem" }}>
        <p style={{ margin: 0, color: "#F9A8D4", fontSize: ".75rem", letterSpacing: ".14em", textTransform: "uppercase" }}>Website content</p>
        <h1 id="website-content-title" style={{ margin: ".25rem 0 .5rem", color: "#FFFFFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.9rem,4vw,2.6rem)", fontWeight: 400 }}>Every page, and where to change it</h1>
        <p style={{ margin: 0, maxWidth: 680, color: "#94A3B8", lineHeight: 1.6, fontSize: "var(--chi-body-compact)" }}>
          Edit opens the editor that already owns that content. Changes are saved there, not here. Pages marked "Written in code" change through a code review.
        </p>
      </header>

      <div style={{ maxWidth: 920, marginBottom: "1.25rem" }}>
        <h2 style={{ margin: "0 0 .55rem", color: "#FFFFFF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "1.05rem" }}>Home feature</h2>
        <HomepagePromotionControl />
      </div>

      <div role="group" aria-label="Filter pages" style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", marginBottom: "1rem" }}>
        {FILTERS.map(item => (
          <button
            key={item.id}
            type="button"
            aria-pressed={filter === item.id}
            onClick={() => setFilter(item.id)}
            style={{
              minHeight: 40, padding: ".4rem .9rem", borderRadius: 999, cursor: "pointer",
              border: `1px solid ${filter === item.id ? "rgba(233,30,140,.6)" : "rgba(255,255,255,.12)"}`,
              background: filter === item.id ? "rgba(233,30,140,.14)" : "rgba(255,255,255,.03)",
              color: filter === item.id ? "#FBCFE8" : "#CBD5E1", fontSize: ".85rem",
            }}
          >
            {item.label}
          </button>
        ))}
      </div>

      <ul aria-label="Website pages" style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: ".75rem" }}>
        {pages.map(page => (
          <li
            key={page.id}
            data-testid={`website-page-${page.id}`}
            style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) auto", gap: ".75rem 1rem", alignItems: "center", padding: "1rem 1.1rem", borderRadius: ".9rem", background: "rgba(255,255,255,.028)", border: "1px solid rgba(255,255,255,.09)" }}
            className="website-content__row"
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: ".5rem" }}>
                <h2 style={{ margin: 0, color: "#FFFFFF", fontSize: "1.05rem", fontWeight: 600 }}>{page.title}</h2>
                <span style={{ padding: ".1rem .55rem", borderRadius: 999, background: "rgba(139,92,246,.14)", border: "1px solid rgba(139,92,246,.3)", color: "#C4B5FD", fontSize: ".72rem" }}>{AUDIENCE_LABEL[page.audience]}</span>
              </div>
              <code style={{ display: "block", marginTop: ".25rem", color: "#94A3B8", fontSize: ".8rem", overflowWrap: "anywhere" }}>{page.path}</code>
              <p style={{ margin: ".4rem 0 0", color: "#CBD5E1", fontSize: "var(--chi-body-dense)", lineHeight: 1.5 }}>{page.editable}</p>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", justifyContent: "flex-end" }} className="website-content__actions">
              <a
                href={page.livePath}
                target="_blank"
                rel="noreferrer"
                aria-label={`View ${page.title} on the site (opens in a new tab)`}
                style={{ display: "inline-flex", alignItems: "center", gap: ".35rem", minHeight: 40, padding: ".4rem .8rem", borderRadius: ".6rem", border: "1px solid rgba(255,255,255,.14)", color: "#CBD5E1", textDecoration: "none", fontSize: ".85rem" }}
              >
                <ExternalLink size={14} aria-hidden="true" /> View
              </a>
              {page.editor ? (
                <button
                  type="button"
                  onClick={() => onOpenEditor(page.editor!.view)}
                  aria-label={`Edit ${page.title}: ${page.editor.label}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: ".35rem", minHeight: 40, padding: ".4rem .9rem", borderRadius: ".6rem", border: "1px solid rgba(233,30,140,.5)", background: "linear-gradient(135deg,#E91E8C,#C2185B)", color: "#FFFFFF", cursor: "pointer", fontSize: ".85rem", fontWeight: 600 }}
                >
                  <FilePenLine size={14} aria-hidden="true" /> Edit
                </button>
              ) : (
                <span style={{ display: "inline-flex", alignItems: "center", gap: ".35rem", minHeight: 40, padding: ".4rem .8rem", color: "#64748B", fontSize: ".82rem" }}>
                  <Lock size={13} aria-hidden="true" /> Written in code
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
      <p style={{ margin: "1rem 0 0", color: "#64748B", fontSize: ".8rem", display: "flex", gap: ".4rem", alignItems: "center" }}>
        <Globe size={13} aria-hidden="true" /> {pages.length} of {WEBSITE_CONTENT_PAGES.length} pages
      </p>
    </section>
  );
}
